import { useEffect, useState } from "react";
import { DEFAULT_MONTHLY_GOAL } from "../config/funnel-targets";
import { normalizeWeeklyTargets, type WeeklyCashTargets } from "../lib/cash-target";

// The shared cash collected target for Velocity Metrics (copied from the Cr8Her dashboard's GoalTargetsPanel.useMonthlyGoal,
// Joey Forslund, Oct 29, 2026: Urban Unity's Velocity Metrics must match Cr8Her's exactly). The monthly goal plus per-week
// overrides are stored in Urban Unity's own KPI targets collection: period "velocity_cash_monthly" and
// "velocity_cash_week:<Monday>" (0 = no override). Read: GET /api/velocity-metrics?targets=1. Write: POST /api/kpi.
export const TARGETS_URL = "/api/velocity-metrics?targets=1";
export const MONTHLY_PERIOD = "velocity_cash_monthly";
export const WEEK_PERIOD_PREFIX = "velocity_cash_week:";

const GOAL_EVENT = "uu:velocity-monthly-goal";
let goalCache: number | null = null;
let weeklyCache: WeeklyCashTargets | null = null;
type GoalDetail = { goal: number; weekly: WeeklyCashTargets };

/** The shared cash collected target: the monthly goal plus per-week overrides (lib/cash-target.ts), loaded from the
 *  server, saved to the server, broadcast to every panel and editor on the page. */
export function useMonthlyGoal() {
  const [goal, setGoal] = useState<number>(goalCache ?? DEFAULT_MONTHLY_GOAL);
  const [weekly, setWeekly] = useState<WeeklyCashTargets>(weeklyCache ?? {});
  const [loaded, setLoaded] = useState(goalCache !== null);
  useEffect(() => {
    const onChange = (event: Event) => {
      const detail = (event as CustomEvent<GoalDetail | number>).detail;
      const next = typeof detail === "number" ? detail : Number(detail?.goal);
      if (next > 0) { setGoal(next); setLoaded(true); }
      if (detail && typeof detail === "object") setWeekly(detail.weekly ?? {});
    };
    window.addEventListener(GOAL_EVENT, onChange);
    const abort = new AbortController();
    fetch(TARGETS_URL, { signal: abort.signal, cache: "no-store" }).then((r) => r.json()).then((body) => {
      const next = Number(body?.monthlyTarget);
      if (body?.success && next > 0) { goalCache = next; setGoal(next); }
      if (body?.success) { weeklyCache = normalizeWeeklyTargets(body.weeklyTargets); setWeekly(weeklyCache); }
      setLoaded(true);
    }).catch(() => setLoaded(true));
    return () => { window.removeEventListener(GOAL_EVENT, onChange); abort.abort(); };
  }, []);
  const apply = (body: any, fallbackGoal: number) => {
    const saved = Number(body.monthlyTarget) || fallbackGoal;
    const savedWeekly = normalizeWeeklyTargets(body.weeklyTargets);
    goalCache = saved; weeklyCache = savedWeekly; setGoal(saved); setWeekly(savedWeekly);
    window.dispatchEvent(new CustomEvent<GoalDetail>(GOAL_EVENT, { detail: { goal: saved, weekly: savedWeekly } }));
    return saved;
  };
  // Saves through the dashboard's existing KPI targets store (POST /api/kpi, admin only), then re-reads the targets.
  const put = async (payload: { monthlyTarget?: number; weekStart?: string; weeklyTarget?: number | null }) => {
    const body = payload.weekStart ? { period: `${WEEK_PERIOD_PREFIX}${payload.weekStart}`, target: Math.max(0, Math.round(Number(payload.weeklyTarget) || 0)) } : { period: MONTHLY_PERIOD, target: Math.round(Number(payload.monthlyTarget) || 0) };
    const response = await fetch("/api/kpi", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
    const saved = await response.json().catch(() => ({}));
    if (!response.ok || !saved.success) throw new Error(saved.error || (response.status === 401 ? "Sign in to change the target" : response.status === 403 ? "Only an admin can change the target" : "Unable to save the target"));
    const read = await fetch(TARGETS_URL, { cache: "no-store" }).then((r) => r.json()).catch(() => null);
    if (!read?.success) throw new Error("Saved, but the target could not be re-read; reload the page");
    return read;
  };
  const save = async (next: number) => apply(await put({ monthlyTarget: next }), next);
  /** Sets (dollars) or clears (null) one week's cash collected target; weekStart = that week's Monday (Eastern). */
  const saveWeek = async (weekStart: string, weeklyTarget: number | null) => { apply(await put({ weekStart, weeklyTarget }), goal); };
  return { goal, weekly, loaded, save, saveWeek };
}

